import { Formik, Form, Field, useField } from "formik";
import * as Yup from "yup";
import { Form as BsForm } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { TextInputComp, FormikTextAreaComp } from "..";
import InvestorModal, { InvestorModalActions } from "./InvestorModal";
import { formatMoney } from "../../utils/currencyFormat";
import { useCreateRealEstateRequestMutation } from "../../store/investor/investorRealEstateApi";
import { buildCreateRealEstatePayload } from "../../utils/investorRealEstateAdapter";

const CONTACT_OPTIONS = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
];

const INSPECTION_OPTIONS = [
  { value: "weekend", label: "Weekend" },
  { value: "weekday", label: "Weekday" },
  { value: "flexible", label: "Flexible" },
];

const schema = Yup.object({
  units: Yup.number().required("Units / plots required").min(1, "At least 1"),
  full_name: Yup.string().required("Full name is required"),
  phone: Yup.string().required("Phone is required"),
  preferred_contact: Yup.string().required("Preferred contact is required"),
  inspection_preference: Yup.string().required("Inspection preference is required"),
  documentation_interest: Yup.boolean(),
  note: Yup.string(),
});

function profileFullName(profile) {
  if (!profile) return "";
  return (
    profile.full_name ||
    profile.name ||
    `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
  );
}

function SelectField({ label, name, options }) {
  const [field, meta] = useField(name);
  return (
    <div className="mb-3">
      <BsForm.Label className="small fw-semibold">{label}</BsForm.Label>
      <BsForm.Select {...field} className="py-2">
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </BsForm.Select>
      {meta.touched && meta.error ? (
        <div className="text-danger small mt-1">{meta.error}</div>
      ) : null}
    </div>
  );
}

export default function RealEstateRequestModal({ show, onClose, property, profile, onSuccess }) {
  const [createRequest, { isLoading }] = useCreateRealEstateRequestMutation();

  if (!property) return null;

  const initial = {
    units: 1,
    full_name: profileFullName(profile),
    phone: profile?.phone || "",
    email: profile?.email || "",
    preferred_contact: "phone",
    inspection_preference: "weekend",
    documentation_interest: true,
    note: "",
  };

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const body = buildCreateRealEstatePayload(values, property);
          const created = await createRequest(body).unwrap();
          toast.success(`Request submitted · ${created.reference}`);
          onSuccess?.(created);
          onClose();
        } catch (error) {
          toast.error(error?.data?.message || "Unable to submit property request.");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, isSubmitting, submitForm }) => {
        const units = Number(values.units) || 1;
        const estimate = units * (property.pricePerUnitNgn || 0);

        return (
          <InvestorModal
            show={show}
            onClose={onClose}
            title="Request property"
            size="md"
            footer={
              <InvestorModalActions
                onCancel={onClose}
                onConfirm={submitForm}
                confirmText={isLoading || isSubmitting ? "Submitting…" : "Submit request"}
                loading={isLoading || isSubmitting}
                confirmDisabled={isLoading || isSubmitting}
              />
            }
          >
            <Form>
              <p className="fw-semibold mb-1" style={{ color: "#383838" }}>
                {property.name}
              </p>
              <p className="text-muted small mb-3">
                {property.location} · {formatMoney(property.pricePerUnitNgn)} / {property.unitLabel}
              </p>
              <TextInputComp label={`Units / ${property.unitLabel}s`} name="units" type="number" min={1} />
              <div className="d-flex justify-content-between small mb-3">
                <span className="text-muted">Estimated value (indicative)</span>
                <strong>{formatMoney(estimate)}</strong>
              </div>
              <TextInputComp label="Full name" name="full_name" />
              <TextInputComp label="Phone" name="phone" />
              <TextInputComp label="Email (optional)" name="email" type="email" />
              <SelectField label="Preferred contact" name="preferred_contact" options={CONTACT_OPTIONS} />
              <SelectField
                label="Inspection preference"
                name="inspection_preference"
                options={INSPECTION_OPTIONS}
              />
              <div className="form-check mb-3">
                <Field name="documentation_interest">
                  {({ field, form }) => (
                    <label className="form-check-label d-flex align-items-center gap-2" htmlFor="documentation_interest">
                      <input
                        id="documentation_interest"
                        className="form-check-input"
                        type="checkbox"
                        checked={Boolean(field.value)}
                        onChange={(e) => form.setFieldValue("documentation_interest", e.target.checked)}
                      />
                      <span className="small" style={{ color: "#383838" }}>
                        I am interested in documentation support
                      </span>
                    </label>
                  )}
                </Field>
              </div>
              <FormikTextAreaComp label="Note (optional)" name="note" rows={3} />
            </Form>
          </InvestorModal>
        );
      }}
    </Formik>
  );
}
